import React, {useCallback, useEffect, useMemo, useState} from 'react';

import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DisabledByDefaultRoundedIcon from '@mui/icons-material/DisabledByDefaultRounded';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import TimelineIcon from '@mui/icons-material/Timeline';
import TheatersIcon from '@mui/icons-material/Theaters';

import {useStores} from '../../../Hooks';
import {accessItemTypeEnum, accessIncludeLevelEnum, procedureTypeEnum} from '../../../serverClientShared/sharedEnums';
import {ThreeDIcon} from '../../../assets/svg-icons/ThreeDIcon';
import {ContentRequester} from '../../Domain/Groups/ContentRequester';
import ContentTreeContext from '../../Generic/ContentTree/ContentTreeContext';
import ChildrenList from '../../Generic/ContentTree/ChildrenList';
import useSingleProjectTree from './useSingleProjectTree';
import useRoleOverrides from './useRoleOverrides';

const infoBannerStyle = {
    backgroundColor: 'color-mix(in srgb, var(--link-normal-color) 8%, transparent)',
    borderColor: 'color-mix(in srgb, var(--link-normal-color) 20%, transparent)',
};
const checkedIcon = <CheckBoxIcon style={{color: 'var(--link-normal-color)'}} />;
const uncheckedItemIcon = <DisabledByDefaultRoundedIcon style={{color: 'var(--leavedeletenew-normal-color)'}} />;
const folderSx = {'&.Mui-checked': {color: 'var(--link-normal-color)'}, color: 'var(--main-light-color)'};

const ICON_BY_PROCEDURE_TYPE = {
    [procedureTypeEnum.REGULAR]: TimelineIcon,
    [procedureTypeEnum.MEDIA]: TheatersIcon,
    [procedureTypeEnum.FUSION]: ThreeDIcon,
    [procedureTypeEnum.THREE_D]: ThreeDIcon,
    [procedureTypeEnum.DESIGNER]: TheatersIcon,
};

const getNodeIcon = (node) => {
    if (node.procedureType === null || node.procedureType === undefined) return undefined;
    return ICON_BY_PROCEDURE_TYPE[node.procedureType] || undefined;
};

const RoleAccessDialog = ({role, projectId, workspace, currentDiffs, folderCounts, onSave, onClose}) => {
    const {generalStore} = useStores();
    const t = generalStore.translate.bind(generalStore);
    const roleName = generalStore.translateRole(role);
    const tree = useSingleProjectTree(workspace, projectId);
    const {isPermitted, toggleItem, toggleItems, toggleFolder, applyAll, buildDiffs, getFolderCheckState} =
        useRoleOverrides(role, tree.loadedChildren, currentDiffs, folderCounts);
    const [isToggling, setIsToggling] = useState(false);

    const handleSave = useCallback(() => onSave(buildDiffs()), [onSave, buildDiffs]);

    const groupByParent = useCallback((folders, items, rootId) => {
        const entries = {[rootId]: {folders: [], items: []}};
        for (const folder of folders) {
            const parentId = folder.ParentFolder || rootId;
            if (!entries[parentId]) entries[parentId] = {folders: [], items: []};
            entries[parentId].folders.push(folder);
            if (!entries[folder.id]) entries[folder.id] = {folders: [], items: []};
        }
        for (const item of items) {
            const parentId = item.ParentFolder || rootId;
            if (!entries[parentId]) entries[parentId] = {folders: [], items: []};
            entries[parentId].items.push(item);
        }
        return entries;
    }, []);

    useEffect(() => {
        let cancelled = false;
        const prefetchAll = async () => {
            try {
                const result = await ContentRequester.getChildrenFoldersAndProcedures(workspace, {
                    parentId: projectId, parentType: accessItemTypeEnum.PROJECT, nestedRoute: projectId,
                    skip: 0, limit: 10000, includePublishedRoles: true,
                    includeLevel: accessIncludeLevelEnum.ALL_DESCENDANTS,
                });
                if (cancelled) return;
                tree.bulkSetChildren(groupByParent(result?.folders || [], result?.items || [], projectId));
            } catch (error) {
                console.error('Failed to prefetch project tree:', error);
            }
        };
        prefetchAll();
        return () => {
            cancelled = true;
        };
    }, [workspace, projectId, groupByParent, tree.bulkSetChildren]);

    const handleToggleFolder = useCallback(async (node, nestedRoute) => {
        if (tree.loadedChildren[node.id]) {
            toggleFolder(node.id);
            return;
        }
        setIsToggling(true);
        try {
            const result = await ContentRequester.getChildrenFoldersAndProcedures(workspace, {
                parentId: node.id, parentType: accessItemTypeEnum.FOLDER, nestedRoute,
                skip: 0, limit: 10000, includePublishedRoles: true,
                includeLevel: accessIncludeLevelEnum.ALL_DESCENDANTS,
            });
            const allFolders = result?.folders || [];
            const allItems = result?.items || [];
            tree.bulkSetChildren(groupByParent(allFolders, allItems, node.id));
            const allChecked = allItems.length > 0 && allItems.every((item) => isPermitted(item.id));
            toggleItems(allItems, !allChecked, [node.id, ...allFolders.map((f) => f.id)]);
        } catch (error) {
            console.error('Failed to load folder items:', error);
        } finally {
            setIsToggling(false);
        }
    }, [tree, workspace, toggleFolder, toggleItems, isPermitted, groupByParent]);

    const renderNodeSuffix = useCallback((node, nodeType, nestedRoute) => {
        switch (nodeType) {
            case accessItemTypeEnum.ITEM:
                return (
                    <Checkbox
                        size="small" checked={isPermitted(node.id)} onChange={() => toggleItem(node.id)}
                        checkedIcon={checkedIcon} icon={uncheckedItemIcon} style={{padding: 2}}
                    />
                );
            case accessItemTypeEnum.FOLDER: {
                const {checked, indeterminate} = getFolderCheckState(node.id);
                return (
                    <Checkbox
                        size="small" checked={checked} indeterminate={indeterminate}
                        checkedIcon={checkedIcon} icon={uncheckedItemIcon}
                        onChange={() => handleToggleFolder(node, nestedRoute)} sx={folderSx} style={{padding: 2}}
                    />
                );
            }
            default:
                return null;
        }
    }, [isPermitted, toggleItem, getFolderCheckState, handleToggleFolder]);

    const ctx = useMemo(() => ({
        childrenCache: tree.loadedChildren,
        expandedNodes: tree.expandedNodes,
        loadingNodes: tree.loadingNodes,
        handleExpand: tree.handleExpand,
        visibleCounts: {},
        hasMoreChildren: () => false,
        handleLoadMoreChildren: () => {},
        renderNodePrefix: undefined,
        renderNodeSuffix,
        getNodeClassName: undefined,
        getNodeIcon,
    }), [tree, renderNodeSuffix]);

    return (
        <Dialog open onClose={onClose} maxWidth={false} PaperProps={{className: 'rounded-lg', style: {width: 520, maxHeight: '80vh', display: 'flex', flexDirection: 'column'}}}>
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
                <span className="text-lg font-semibold text-main-normal">
                    {t('configureAccessFor') || 'Configure Access for'} {roleName}
                </span>
                <CloseIcon onClick={onClose} style={{fontSize: 20, cursor: 'pointer', color: 'var(--main-light-color)'}} />
            </div>

            <div className="mx-5 mb-3 px-3 py-2.5 rounded-input border flex items-start gap-2" style={infoBannerStyle}>
                <InfoOutlinedIcon style={{fontSize: 18, color: 'var(--link-normal-color)', marginTop: 1}} />
                <span className="text-xs text-main-normal">
                    {t('selectWhichFoldersAndItemsTheRole') || 'Select which folders and items the role'}{' '}
                    <strong>{roleName}</strong>{' '}
                    {t('canAccess') || 'can access'}
                </span>
            </div>

            <div className="flex items-center gap-3 mx-5 mb-2">
                <span className="text-sm text-link-normal cursor-pointer hover:underline" onClick={() => applyAll(true)}>
                    {t('selectAll') || 'Select All'}
                </span>
                <span className="text-sm text-link-normal cursor-pointer hover:underline" onClick={() => applyAll(false)}>
                    {t('deselectAll') || 'Deselect All'}
                </span>
            </div>

            <div className="flex-1 overflow-y-auto mx-5 mb-3 px-1 relative">
                {isToggling && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
                        <CircularProgress size={24} />
                    </div>
                )}
                <ContentTreeContext.Provider value={ctx}>
                    <ChildrenList parentNestedRoute={projectId} nodeId={projectId} depth={-1} />
                </ContentTreeContext.Provider>
            </div>

            <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-panels-normal">
                <span className="text-sm text-main-normal cursor-pointer hover:underline" onClick={onClose}>
                    {t('cancel') || 'Cancel'}
                </span>
                <Button variant="contained" size="small" onClick={handleSave} sx={{textTransform: 'none', borderRadius: 2}}>
                    {t('saveChanges') || 'Save Changes'}
                </Button>
            </div>
        </Dialog>
    );
};

export default RoleAccessDialog;
